import { useGetClinicQuery } from "services/clinics";
import type { Clinic } from "services/types/clinics.type";

type UseFormClinicParams = {
  /** The clinic the form belongs to: the sale's, the order's ship-to or the repair dialog's. */
  clinicId?: number;
  /** The surface's flag is on and a Digital Form is picked. */
  enabled: boolean;
};

/**
 * ENG-2491: the full clinic a Digital Form's Clinic.* and Practice.* tags read (handed to them as
 * `formClinic`). Fetched with the same query the top-bar clinic is loaded with, because the dialogs'
 * own clinic objects lack fields the tags print (email, fax, billing, NPI).
 *
 * `formClinic` is only ever the clinic asked for: `skip` leaves the last response in place, so a
 * disabled surface or a clinic still loading after a switch never hands over another clinic's data.
 * If the clinic can't be fetched, there is nothing to wait for and the tags print the top-bar clinic.
 */
export const useFormClinic = ({
  clinicId,
  enabled
}: UseFormClinicParams): { formClinic?: Clinic; isLoading: boolean } => {
  const skip = !enabled || !clinicId;
  const { currentData, isFetching } = useGetClinicQuery(
    { clinicId: clinicId ?? 0 },
    { skip }
  );

  const formClinic =
    !skip && currentData?.id === clinicId ? currentData : undefined;

  return { formClinic, isLoading: !skip && isFetching && !formClinic };
};
